import React, { useContext, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { ModalDialog, FlowForm, getKeyHandler, useModal, registerKeyHandler, unregisterKeyHandler } from 't-lib-ui/structure';
import { TextButton, Input } from 't-lib-ui/widgets';
import { globalFocus } from 't-lib-ui/helpers';
import { AppState } from '../context/general';
import { getNamedFolders, moveAfterSubscribe, cleanFolderName, placePopover, MAX_FOLDER_NAME_LENGTH } from './folderPicker';
import { MAX_FOLDERS } from './sidebarFolders';
import FolderIcon from './FolderIcon';
import FolderPlusIcon from './FolderPlusIcon';
import './FolderPickerPopup.css';

/** Below this width the picker is a modal dialog instead of a popover (same breakpoint as Popup). */
const SMALL_SCREEN_QUERY = '(max-width: 600px)';

const itemKeyHandler = (onAction: () => void) =>
  getKeyHandler({ enterBehaviour: 'action', spaceBehaviour: 'action', onAction }) as any;

interface ContentProps {
  folders: string[];
  canCreate: boolean;
  /** Called with the chosen (possibly new) folder, or undefined for "no folder". */
  onPick: (folder?: string) => void;
}

/** The folder list shared by popover and modal: folders, "New folder…" and "No folder". */
const FolderPickerContent: React.FC<ContentProps> = ({ folders, canCreate, onPick }) => {
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const newRowRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (creating) newRowRef.current?.querySelector('input')?.focus();
  }, [creating]);

  const create = () => {
    const name = cleanFolderName(newName);
    if (name) onPick(name);
  };

  const item = (key: string, label: string, icon: React.ReactNode, onClick: () => void, className = '') => (
    <button
      key={key}
      type="button"
      className={`folderPickerItem flowFocussable ${className}`}
      tabIndex={0}
      onKeyDown={itemKeyHandler(onClick)}
      onClick={onClick}
    >
      {icon}
      <span className="folderPickerName">{label}</span>
    </button>
  );

  return (
    <div className="folderPickerList">
      {folders.map(folder => item(folder, folder, <FolderIcon size={18} className="folderPickerIcon" />, () => onPick(folder)))}
      {canCreate && (creating
        ? <div className="folderPickerNew" ref={newRowRef}>
            <Input
              value={newName}
              onInput={setNewName}
              onEnter={create}
              placeholder="Folder name"
              maxLength={MAX_FOLDER_NAME_LENGTH}
              enterKeyHint="enter"
              autoCapitalize={true}
              layout="singleCol"
              className="folderPickerNewInput"
            />
            <TextButton type="major" size="small" focussable="sideway" label="Create" onClick={create}
                        enabled={cleanFolderName(newName) ? 'enabled' : 'disabled'} />
          </div>
        : item('+new', 'New folder…', <span className="folderPickerIcon"><FolderPlusIcon size={18} /></span>, () => setCreating(true)))}
      <div className="folderPickerDivider" />
      {item('+none', 'No folder', <span className="folderPickerIcon" />, () => onPick(), 'folderPickerNone')}
    </div>
  );
};

interface FolderPickerProps extends ContentProps {
  /** Changes on every open, so a re-opened picker starts fresh. */
  token: number;
  /** What is being followed, e.g. a feed title or collection name (modal only). */
  subject?: string;
  /** Element id of the Follow button; set for the popover, unset for the modal. */
  anchorId?: string;
}

/**
 * Non-modal popover under the Follow button. It keeps tracking the button while the page scrolls
 * and stays where it was if the button disappears (e.g. the page switched views after the follow).
 * Its content is a FlowForm of its own: Up/Down move between items, and Up from the first item or
 * Escape close it (meaning "no folder") and return focus to the button. So does a click outside.
 */
const FolderPickerPopover: React.FC<FolderPickerProps & { anchorId: string }> = ({ token, anchorId, subject: _subject, onPick, ...content }) => {
  const ref = useRef<HTMLDivElement>(null);
  const keyHandlerId = 'folderPicker-' + useId();

  // Hands focus back to the Follow button once the popover is gone (if the button still exists).
  const pick = (folder?: string) => {
    onPick(folder);
    if (document.getElementById(anchorId)) globalFocus(anchorId);
  };
  const pickRef = useRef(pick);
  pickRef.current = pick;

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const place = () => {
      const anchor = document.getElementById(anchorId);
      if (!anchor) return;
      const { left, top } = placePopover(anchor.getBoundingClientRect(), { width: el.offsetWidth, height: el.offsetHeight },
                                         { width: window.innerWidth, height: window.innerHeight });
      el.style.left = `${left}px`;
      el.style.top = `${top}px`;
    };
    place();

    // Match the page's focus mode, so a keyboard-triggered popover shows its focus ring right away.
    const keyboardMode = !!document.querySelector('.flowForm.visibleFocus');
    el.querySelector('.flowForm')?.classList.add(keyboardMode ? 'visibleFocus' : 'hiddenFocus');
    el.querySelector<HTMLElement>('.flowFocussable')?.focus({ preventScroll: true });

    const outside = (ev: PointerEvent) => { if (!el.contains(ev.target as Node)) pickRef.current(); };
    // Re-place when the content changes size, e.g. when "New folder" turns into an input.
    const resizeObserver = new ResizeObserver(place);
    resizeObserver.observe(el);
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    document.addEventListener('pointerdown', outside, true);
    registerKeyHandler(keyHandlerId, { Escape: () => { pickRef.current(); return true; } }, false, 1002);
    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
      document.removeEventListener('pointerdown', outside, true);
      unregisterKeyHandler(keyHandlerId);
    };
  }, [token, anchorId, keyHandlerId]);

  return (
    <div ref={ref} className="folderPickerPopover" role="dialog" aria-label="Add to folder">
      <div className="folderPickerTitle">Add to folder</div>
      {/* Up from the first item closes the popover and lands on the Follow button again. The
          element must be returned, else FlowForm would jump into the previous form on the page. */}
      <FlowForm onLeaveForm={dir => {
        if (dir != 'up') return;
        onPick();
        return document.getElementById(anchorId) ?? document.body;
      }}>
        <FolderPickerContent key={token} {...content} onPick={pick} />
      </FlowForm>
    </div>
  );
};

const FolderPicker: React.FC<FolderPickerProps> = props => {
  const { anchorId, subject, onPick, token, folders, canCreate } = props;
  if (anchorId) return <FolderPickerPopover {...props} anchorId={anchorId} />;
  return (
    <ModalDialog
      show={true}
      title="Add to Folder"
      closeButton="close"
      onClose={() => onPick()}
      closeOnBackgroundClick={true}
      className="folderPickerModal"
      minWidth="340px"
    >
      <FlowForm className="folderPickerModalBody">
        <p className="folderPickerIntro">
          {subject ? <>Add <strong>{subject}</strong> to a folder?</> : 'Add to a folder?'}
        </p>
        <FolderPickerContent key={token} folders={folders} canCreate={canCreate} onPick={onPick} />
      </FlowForm>
    </ModalDialog>
  );
};

export interface AskFolderOptions {
  feedIds: string[];
  /** The already running follow; the move into the chosen folder waits for it. */
  subscription: Promise<unknown>;
  /** What is being followed, e.g. a feed title or collection name. */
  subject?: string;
  /** Element id of the clicked Follow button. On larger screens the picker pops up below it. */
  anchorId?: string;
}

/**
 * Returns a function to call right after starting a follow: when the user has folders, it asks
 * which one to put the new feed(s) into - or a new one. The follow itself is already running;
 * moving into the chosen folder is a second step once it completes. The picker is hosted by the
 * app-wide ModalContainer, so it stays open even if the calling component unmounts after the follow.
 */
export function useFolderPicker(): (options: AskFolderOptions) => void {
  const { feedEngine } = useContext(AppState);
  const [showModal, dismissModal] = useModal(FolderPicker);
  const tokenRef = useRef(0);

  return ({ feedIds, subscription, subject, anchorId }) => {
    const folders = getNamedFolders(feedEngine);
    if (!folders.length || !feedIds.length) return;
    const popover = !!anchorId && !!document.getElementById(anchorId) && !window.matchMedia(SMALL_SCREEN_QUERY).matches;
    showModal({
      token: ++tokenRef.current,
      folders,
      canCreate: folders.length < MAX_FOLDERS,
      subject,
      anchorId: popover ? anchorId : undefined,
      onPick: folder => {
        dismissModal();
        if (folder)
          moveAfterSubscribe(feedEngine, feedIds, subscription, folder)
            .catch(e => console.error('Moving followed feed into folder failed:', e));
      },
    });
  };
}

export default FolderPicker;
